import type { CanvasWorld } from '@streamlain/canvas-selection-contract';

import type { Doc, GetStoreOptions, RemoveStoreOptions } from '../extension';
import { createDocBindingExtension } from '../extension/workspace';
import { type Query, Store } from './store';

export class StoreContainer {
  private readonly _storeMap = new Map<string, Store>();

  constructor(readonly doc: Doc) {}

  getStore = ({
    readonly,
    query,
    provider,
    extensions,
    id,
    canvasWorld = 'bounded',
  }: GetStoreOptions = {}) => {
    let idOrOptions:
      | string
      | { readonly?: boolean; query?: Query; canvasWorld: CanvasWorld };
    // An unbounded store is its own view of the doc, like a readonly one.
    if (readonly || query || canvasWorld !== 'bounded') {
      idOrOptions = { readonly, query, canvasWorld };
    } else if (!id) {
      idOrOptions = this.doc.workspace.idGenerator();
    } else {
      idOrOptions = id;
    }
    const key = this._getQueryKey(idOrOptions);

    if (this._storeMap.has(key)) {
      return this._storeMap.get(key) as Store;
    }

    const doc = new Store({
      doc: this.doc,
      readonly,
      query,
      provider,
      canvasWorld,
      extensions: [createDocBindingExtension(this.doc), ...(extensions ?? [])],
    });

    this._storeMap.set(key, doc);

    return doc;
  };

  removeStore = ({
    readonly,
    query,
    id,
    canvasWorld = 'bounded',
  }: RemoveStoreOptions) => {
    let idOrOptions:
      | string
      | { readonly?: boolean; query?: Query; canvasWorld: CanvasWorld };
    if (readonly || query || canvasWorld !== 'bounded') {
      idOrOptions = { readonly, query, canvasWorld };
    } else if (!id) {
      return;
    } else {
      idOrOptions = id;
    }
    const key = this._getQueryKey(idOrOptions);
    this._storeMap.delete(key);
  };

  private readonly _getQueryKey = (
    idOrOptions:
      | string
      | { readonly?: boolean; query?: Query; canvasWorld: CanvasWorld }
  ) => {
    if (typeof idOrOptions === 'string') {
      return idOrOptions;
    }
    const { readonly, query, canvasWorld } = idOrOptions;
    const readonlyKey = this._getReadonlyKey(readonly);
    const key = JSON.stringify({
      readonlyKey,
      query,
      canvasWorld,
    });
    return key;
  };

  private _getReadonlyKey(readonly?: boolean): 'true' | 'false' {
    return (readonly?.toString() as 'true' | 'false') ?? 'false';
  }
}
